<template>
  <div
    data-cy="list-item"
    :class="card-container"
    :style="[
      {
        padding: '0',
        boxShadow: `0 ${$getScaledCssValue('6px')} ${$getScaledCssValue(
          '20px'
        )} #AAAAAA65`,
        marginBottom: $getScaledCssValue('22px'),
        display: 'flex',
        alignItems: 'stretch', // Ensure image and content are the same height
        flexDirection: $breakpoints.smAndDown ? 'column' : 'row', // Side by side for larger screens
        borderRadius: $getScaledCssValue('8px'), // Add rounded corners
        overflow: 'hidden', // Ensure corners apply to the image as well
        height: $getScaledCssValue('200px')
      }
    ]"
  >

   <!-- Image Section -->
   <div
      class="image-container"
      :style="{
        flexBasis: '20%',
        overflow: 'hidden',
        flexShrink: 0
      }"
    >
    <SkeletonLoader
    :showItem="item && (item.thumbnail_link || item.submission_type?.name)"
    type="rect"
    :style="{
      width: '100%',
      height: '100%',
      backgroundColor: '#f0f0f0',
      borderRadius: $getScaledCssValue('8px')
    }"
  >
    <template #item>

    <img
      v-if="item.thumbnail_link"
      :src="item.thumbnail_link"
      alt="Card Image"
      :style="{
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }"
    />
    <v-icon
      v-else
      size="150" 
      color="#ffffff"
      :style="{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        backgroundColor: '#DCDCDC'
      }"
    >{{ setIconPath(item.submission_type.submission_type_category) }}</v-icon>
    </template>
    </SkeletonLoader>
    </div>

    <div
      class="content-container d-flex flex-column flex-grow-1"
      :style="{
        flexBasis: '80%', // Remaining width for content
        padding: '16px', // Add padding inside the content section
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }"
    >
      <div class="d-flex justify-space-between align-center">
        <SkeletonLoader :showItem="item !== null" type="text" width="100" :style="{ width: '100%', minWidth: 0 }">
          <template #item>
            <div
              data-cy="list-item-title"
              class="text-p6"
              :style="{
                color: '#507536',
                whiteSpace: 'nowrap',
                maxWidth: '300px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: 'bold'
              }"
            >
               {{ item.title }}
            </div>
          </template>
        </SkeletonLoader>
        <SkeletonLoader
          v-if="!$breakpoints.smAndDown"
          :showItem="item !== null"
          type="text"
          width="200"
          :style="{
            marginLeft: '8px'
          }"
          ><template #item
            ><div class="text-p4" :style="{ color: color, maxWidth: '300px', minWidth: '180px', textAlign:'right'}">
              <!-- {{ item.date ? item.date : item.type ? item.type : '' }} -->
              {{ item.submission_type.name }}
            </div>
          </template></SkeletonLoader>
      </div>

      <div v-if="!$breakpoints.smAndDown" class="text-mark-thin-16"
        :style="{ marginBottom: $getScaledCssValue('10px') }">
        <SkeletonLoader :showItem="item !== null" type="text" width="200" :style="{
          whiteSpace: 'nowrap',
          maxWidth: '600px',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }">
          <template #item>
            {{ item.additionalInfo }}
          </template>
        </SkeletonLoader>
      </div>

      <SkeletonLoader :showItem="item !== null" type="paragraph">
        <template #item>
          <div
            ref="purpose"
            :class="{
              'd-flex': true,
              'text-mark-light-16': $breakpoints.smAndDown,
              'text-p4': $breakpoints.mdAndUp
            }"
            :style="{
              height: $getScaledCssValue('80px'),
              overflow: 'hidden',
              position: 'relative',

              whiteSpace: 'wrap',
           
              fontSize: $getScaledCssValue('18px'),
              textOverflow: 'ellipsis',

            }"
          >
            {{ truncateByCharacters(item.description, 250) }}
            <div class="purpose-overflow-blur"></div>
          </div>
        </template>
      </SkeletonLoader>

      <div v-if="!$breakpoints.smAndDown" class="
          d-flex
          list-row
          align-center
          justify-space-between
          flex-column flex-md-row
        " :style="{
          marginTop: $getScaledCssValue('11px')
        }">
        <SkeletonLoader :showItem="item !== null" type="text" class="d-flex flex-wrap" width="100" :style="{
          marginTop: $getScaledCssValue('-12px')
        }">
          <template #item>
            <div
              v-for="(tag, index) in JSON.parse(item.tags).slice(0, 5)"
              :key="index"
              class="text-mark-light-16"
              :style="{
                border: '1px solid #DBDBDB',
                borderRadius: $getScaledCssValue('7px'),
                padding: `4px ${$getScaledCssValue('12px')}`,
                marginRight: $getScaledCssValue('12px'),
                marginTop: $getScaledCssValue('12px'),
                color: color,
                fontSize: $getScaledCssValue('14px')
              }"
            >
              {{ tag }}
            </div>
          </template>
        </SkeletonLoader>

        <SkeletonLoader :showItem="item !== null" type="text" width="100">
          <template #item>
            <v-hover #default="{ hover }">
              <div class="d-flex align-center">
                <div data-cy="view-list-item-details" class="text-mark-book-18" @click="navigateToViewMore(item)"
                  :style="{
                    cursor: 'pointer',
                    color: color
                  }">
                  {{ $t("viewMoreMessage") }}
                </div>

                <!-- dialog for show more -->
                <v-dialog v-model="showPopup" max-width="1000">
                  <v-dialog v-model="showPopup" max-width="1000" persistent>
                    <v-card>
                      <div class="d-flex justify-end">
                        <v-btn class="ma-2" outlined fab color="primary" @click="showPopup = false">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-card-text class="pt-1">
                        <viewMoreLayout v-if="showPopup" :title="popupData.title" :description="popupData.description"
                          :imageSrc="popupData.imageSrc" :iconSrc="popupData.iconSrc" :programme="popupData.programme" :project="popupData.project" :uuid="popupData.uuid" :type="popupData.type"
                          :country="popupData.country" :tags="popupData.tags" :abstract="popupData.abstract"  @close="closePopup" />
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" @click="showPopup = false">Close</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </v-dialog>
                <Arrow class="right" :style="{
                  color: color,
                  opacity: hover ? 1 : 0,
                  transition: 'opacity 300ms ease-out'
                }" borderWidth="1px" width="2px" />
              </div>
            </v-hover>
          </template>
        </SkeletonLoader>
      </div>
    </div>

    <div v-if="$breakpoints.smAndDown" class="align-self-center" :style="{ marginLeft: '8px', cursor: 'pointer' }"
      @click="$emit('viewItemClicked')">
      <Arrow class="right secondary--text" borderWidth="1px" width="8px" />
    </div>
  </div>
</template>

<script>
import SkeletonLoader from '../Utils/SkeletonLoader.vue'
import Arrow from '../Utils/Arrow.vue'
import { checkHeightOverflowed } from '../Utils/functions';
import viewMoreLayout from '@/views/viewMoreLayout.vue';

export default {
  components: {
    SkeletonLoader,
    Arrow,
    viewMoreLayout,

  },
  data() {
    return {
      showPopup: false,
      popupData: {},
    };
  },


  props: {
    name: {
      type: String,
      default: null
    },
    additionalInfo: {
      type: String,
      default: null
    },
    date: {
      type: [String, Number],
      default: null
    },
    description: {
      type: String,
      default: null
    },
    tags: {
      type: Array,
      default: null
    },
    type: {
      type: String,
      default: null
    },
    color: {
      type: String,
      default: '#ac832d'
    },
    uuid: {
      type: String,
      default: null
    },
    title:{
      type:String,
      default: null
    },

    item: {
      type: Object,
      default: null
    }
  },
  breakpoints() {
  },
  mounted() {
    window.addEventListener("keydown", this.handleEscape);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleEscape);
  },

  methods: {
    closePopup() {
      this.showPopup = false;
    },
    truncateByCharacters(text, charLimit) {
      if (!text) return "";

      if (text.length > charLimit) {
        return text.slice(0, charLimit) + "...";
      }

      return text;
    },

    handleEscape(event) {
      if (event.key === "Escape") {
        this.closePopup();
      }
    },
    navigateToViewMore(item) {
      // this.$router.push({
      //   name: 'viewMore',
      //   params: {
      //     item: this.item,
      //     uuid: this.item.bitstream_uuid,
      //     type: this.item.submission_type.name,
      //   }
      // })
      this.popupData = {
        title: this.item.title,
        description: item.description,
        imageSrc: item.thumbnail_link,
        iconSrc: this.setIconPath(item.submission_type.submission_type_category),
        programme: item.collection?.community?.name || "NA",
        project: item.collection?.name || "N/A",
        country: item.country?.name || "N/A",
        abstract: item.recent_abstract || "N/A",
        uuid:item.bitstream_uuid,
        type:item.file_type,
        tags: (() => {
          try {
            return JSON.parse(item.tags);
          } catch {
            return [];
          }
        })(),
      };
      this.showPopup = true;
    },
    getFallbackImage() {
      const fallbackImages = {
        Dataset: "data:image/png;base64,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",
        Video: "https://cdn.dribbble.com/users/17914/screenshots/4902225/media/0d6d47739dae97adc81ca7076ee56cc9.png",
        Image: "https://assets.gopromotional.co.uk/images/article-placeholder.jpg",
        Article: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRMbgPNOtKRmcAvBfHasZ9F7G3oNWbYIY1cdA&s",
        MusicalScore: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQfMSWeotwB1QyiECMiv75JqPTVaz5ErrOokA&s"
      };
      return fallbackImages[this.type] || "https://www.aatf-africa.org/wp-content/uploads/2024/01/AATF-Website-Home-Page-Design-December-2023-WELCOME-TO-AATF.png";
    },
    setIconPath(type) {
      console.log("type is "+type);
			if(type == 'Image'){
				return "mdi-image";
			} else if(type == 'Audio' || type == 'Recording, oral' || type == "Recording, musical" || type == "Musical Score"){
				return "mdi-cassette";
			}else if(type == 'Video'){
				return "mdi-video";
			}else if (type == 'Publication'){
				return "mdi-file-document";		
			}else if (type == "Dataset"){
        return "mdi-file-document";
      }else if (type == "Law"){
        return "mdi-gavel";
      }
      return "mdi-file";
		},

  },
}
</script>

<style scoped>
.purpose-overflow-blur {
  position: absolute;
  bottom: 0;
  left: 0;
  /*background: radial-gradient(ellipse at top, transparent, white);*/
  background: linear-gradient(transparent 0%, white 100%);
  display: none;
  width: 100%;
  height: 20px;
}
</style>
